import createTicker from '../helpers/createTicker';

declare global {
    interface Window {
        Intercom?: (action: string, ...args: unknown[]) => void;
        intercomSettings?: Record<string, unknown>;
    }
}

export type LiveChatConfig = {
    apiBase: string;
    appId: string;
    shutdownCookieName: string;
    jwt?: string;
    tokenEndpoint?: string;
};

/**
 * LiveChatLoader owns every Intercom() call in the narwhal app.
 *
 * Branches on the livechat_shutdown_pending cookie:
 *   - absent → set intercomSettings + run installer IIFE (SDK auto-boots from settings)
 *   - present → run installer IIFE WITHOUT intercomSettings; queue shutdown then anonymous boot; delete cookie
 *
 * Bug fix vs Phase 3: by NOT setting intercomSettings on the shutdown branch, the SDK no
 * longer auto-boots with leftover identity cookies before the shutdown queue entry runs.
 */
export default class LiveChatLoader {
    private readonly _ticker: readonly [start: () => void, stop: () => void] | null;

    public constructor(
        private readonly _config: LiveChatConfig,
        private readonly _intervalMs: number = 300000,
    ) {
        const endpoint = _config.tokenEndpoint;
        this._ticker = endpoint
            ? createTicker(async () => {
                  try {
                      if (typeof window.Intercom !== 'function') return;
                      const res = await fetch(endpoint, { credentials: 'same-origin' });
                      if (res.status === 401 || res.status === 403) {
                          // The session no longer belongs to a logged-in client
                          // (logged out, or an applicant journey took over the
                          // shared session — UC-5130). No retry can succeed until
                          // a fresh identified page load, so stop polling.
                          this.stop();
                          return;
                      }
                      if (!res.ok) return;
                      const data = await res.json();
                      if (typeof data?.jwt === 'string') {
                          window.Intercom('update', { intercom_user_jwt: data.jwt });
                      }
                  } catch (err) {
                      // Network/parse/Intercom failures are expected during connectivity
                      // issues — the ticker retries on the next tick. Log at debug level
                      // so the error is captured for anyone investigating connectivity
                      // but doesn't surface in default console output.
                      console.debug('LiveChatLoader refresh tick failed:', err);
                  }
              }, _intervalMs)
            : null;
    }

    public start(): void {
        const cfg = this._config;
        if (!cfg.appId) return;

        const cookiePresent = this._hasShutdownCookie();

        if (!cookiePresent) {
            const settings: Record<string, unknown> = { api_base: cfg.apiBase, app_id: cfg.appId };
            if (cfg.jwt) settings.intercom_user_jwt = cfg.jwt;
            window.intercomSettings = settings;
            this._runInstaller();
        } else {
            // Shutdown signal present. Two cases:
            //   (a) true logout — page emits anonymous config (no cfg.jwt). Wipe Intercom's
            //       session-scoped cookies (belt-and-braces alongside the authoritative
            //       server-side Set-Cookie deletion in the logout/login controller), then
            //       queue shutdown + boot anonymous.
            //   (b) identity swap — logout-then-login leaves the shutdown cookie set on
            //       the post-login (identified) page. Don't wipe any Intercom cookies here:
            //       intercom-id-{appId} is the persistent visitor identifier, and the new
            //       JWT will replace the prior identified session on boot.
            //
            // Either case leaves intercom-id-{appId} intact so the visitor's anonymous
            // conversation history isn't severed (matches the server-side cleanup in
            // narwhal/controller/app/control.login.php and dbsassist/system/form.logout.php
            // which also intentionally omit intercom-id from the deletion list).
            if (!cfg.jwt) {
                this._clearIntercomCookies(cfg.appId);
            }
            this._runInstaller();
            if (typeof window.Intercom === 'function') {
                window.Intercom('shutdown');
                const bootArgs: Record<string, unknown> = {
                    api_base: cfg.apiBase,
                    app_id: cfg.appId,
                };
                if (cfg.jwt) bootArgs.intercom_user_jwt = cfg.jwt;
                window.Intercom('boot', bootArgs);
            }
            document.cookie = cfg.shutdownCookieName + '=; Max-Age=0; path=/; SameSite=Lax';
        }

        if (cfg.tokenEndpoint && this._ticker) {
            this._ticker[0]();
        }
    }

    public stop(): void {
        this._ticker?.[1]();
    }

    private _hasShutdownCookie(): boolean {
        const prefix = this._config.shutdownCookieName + '=';
        return document.cookie
            .split(';')
            .some((c) => c.trim().startsWith(prefix));
    }

    private _clearIntercomCookies(appId: string): void {
        // intercom-id-{appId} is intentionally NOT in this list — it's Intercom's
        // persistent visitor identifier; wiping it would force the user to appear as
        // a brand-new contact in the dashboard on every logout, severing their
        // conversation history. Matches the server-side cleanup in
        // narwhal/controller/app/control.login.php and dbsassist/system/form.logout.php
        // (both intentionally omit intercom-id from the deletion prefix list).
        const names = [
            'intercom-session-' + appId,
            'intercom-device-id-' + appId,
        ];
        // Cookie deletion requires the new Set-Cookie to match (name, path, domain) of the
        // original. Intercom sets these cookies on the parent domain (e.g. ".app.local" for
        // "narwhal.app.local") so they're shared across subdomains. Brute-force every
        // plausible domain — host-only, host with leading dot, and each parent-domain
        // suffix — so at least one variant matches whatever Intercom actually wrote.
        const expired = '; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0';
        const host = window.location.hostname;
        const domains: (string | null)[] = [null, host, '.' + host];
        const parts = host.split('.');
        for (let i = 1; i < parts.length - 1; i++) {
            domains.push('.' + parts.slice(i).join('.'));
        }
        for (const name of names) {
            for (const d of domains) {
                const domainAttr = d === null ? '' : '; domain=' + d;
                document.cookie = name + '=' + expired + '; path=/' + domainAttr;
                document.cookie = name + '=' + expired + '; path=/; SameSite=None; Secure' + domainAttr;
                document.cookie = name + '=' + expired + '; path=/; SameSite=Lax' + domainAttr;
            }
        }
    }

    private _runInstaller(): void {
        // Intercom's official Messenger install snippet — same code that appears
        // at Settings → Installation → JavaScript in every Intercom workspace,
        // also documented at:
        //   https://developers.intercom.com/installing-intercom/web/installation
        //
        // Vendor boilerplate; we don't maintain it. The TypeScript-ification
        // (type guards on `w` / `ic` etc.) is structural only — the runtime
        // behaviour is unchanged from the JS snippet Intercom ships. The only
        // intentional modification is reading APP_ID from config instead of the
        // hard-coded string in the dashboard snippet.
        //
        // If Intercom ever updates this (rare; announced in their changelog at
        // https://www.intercom.com/changelog), the procedure is: copy the fresh
        // dashboard snippet, structurally diff against this function, replace
        // the literal APP_ID with our config read. The structural contract
        // (queue stub at window.Intercom, async script-tag injection of
        // widget.intercom.io/widget/{APP_ID}, reattach_activator on SDK
        // re-mount) is locked by tests in frontendtests/default/app/livechat/
        // LiveChatLoader.test.ts — anything that breaks the contract fails there.
        const APP_ID = this._config.appId;
        (function () {
            const w = window as Window & { Intercom?: unknown };
            const ic = w.Intercom;
            if (typeof ic === 'function') {
                (ic as (action: string, ...args: unknown[]) => void)('reattach_activator');
                (ic as (action: string, ...args: unknown[]) => void)('update', (w as { intercomSettings?: unknown }).intercomSettings);
            } else {
                const d = document;
                type IntercomStub = { (...args: unknown[]): void; q: unknown[][]; c: (args: unknown[]) => void };
                const i = function (this: unknown, ...args: unknown[]) {
                    (i as unknown as IntercomStub).c(args);
                } as unknown as IntercomStub;
                i.q = [];
                i.c = function (args: unknown[]) { i.q.push(args); };
                (w as { Intercom?: unknown }).Intercom = i;
                const l = function () {
                    const s = d.createElement('script');
                    s.type = 'text/javascript';
                    s.async = true;
                    s.src = 'https://widget.intercom.io/widget/' + APP_ID;
                    const x = d.getElementsByTagName('script')[0];
                    x?.parentNode?.insertBefore(s, x);
                };
                if (document.readyState === 'complete') {
                    l();
                } else if ((w as unknown as { attachEvent?: (e: string, fn: () => void) => void }).attachEvent) {
                    (w as unknown as { attachEvent: (e: string, fn: () => void) => void }).attachEvent('onload', l);
                } else {
                    (w as Window).addEventListener('load', l, false);
                }
            }
        })();
    }
}
